/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import CreatableSelect from 'react-select/creatable';
import { MultiValue, SingleValue } from 'react-select';
import Model from '@tripian/model';
import { RSelectOption } from '../base/RSelect/RSelect';
import classes from './PoiSearchAutoComplete.scss';

interface IPoiSearchAutoComplete {
  inputValue: string;
  options: RSelectOption[];
  selectedOptionValues: string[];
  onSelectedOptionChange: (selectedOptions: RSelectOption[]) => void;
  onInputChange?: (input: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onCreateOption: (input: string) => void;
  placeHolder?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  t: (value: Model.TranslationKey) => string;
}

const ITEM_HEIGHT = 35;
const WINDOW_SIZE = 10;
const BUFFER_SIZE = Math.ceil(WINDOW_SIZE * 0.5);
type VirtualizedMenuListProps = {
  children: React.ReactNode;
  maxHeight: number;
};

const VirtualizedMenuList = ({ children: childrenProp, maxHeight }: VirtualizedMenuListProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = React.useState(0);

  const children = React.Children.toArray(childrenProp) as React.ReactNode[];

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const isNoOptionsMessage = (child: React.ReactNode) => {
    if (!React.isValidElement(child) || !('props' in child)) return false;
    const props = child.props as { options?: unknown };
    return Array.isArray(props.options) && props.options.length === 0;
  };

  const totalHeight = children.length * ITEM_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER_SIZE);
  const endIndex = Math.min(startIndex + WINDOW_SIZE + BUFFER_SIZE * 2, children.length);

  const visibleChildren = children.slice(startIndex, endIndex);
  const offsetY = startIndex * ITEM_HEIGHT;

  if (children.length === 1 && isNoOptionsMessage(children[0])) {
    return (
      <div
        ref={menuRef}
        style={{
          maxHeight: 300,
          height: 'auto',
          overflow: 'auto',
          position: 'relative',
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      ref={menuRef}
      style={{
        height: children.length === 1 && isNoOptionsMessage(children[0]) ? 'auto' : maxHeight,
        overflow: 'auto',
        position: 'relative',
        margin: children.length === 1 && isNoOptionsMessage(children[0]) ? '0' : '1rem 0',
      }}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            top: offsetY,
            left: 0,
            right: 0,
          }}
        >
          <div className="visible-children">{visibleChildren}</div>
        </div>
      </div>
    </div>
  );
};

const PoiSearchAutoComplete: React.FC<IPoiSearchAutoComplete> = ({ inputValue, options, selectedOptionValues, onSelectedOptionChange, onInputChange, onKeyDown, onCreateOption, placeHolder, isDisabled = false, isLoading = false, t }) => {
  const [localOptions, setLocalOptions] = React.useState<RSelectOption[]>([]);

  useEffect(() => {
    if (Array.isArray(options) && options.length > 0) {
      setLocalOptions(options);
    }
  }, [options]);

  const selectedOptions = useMemo(() => {
    if (!selectedOptionValues.length) return [];
    return localOptions.filter((option) => selectedOptionValues.includes(option.value));
  }, [selectedOptionValues, localOptions]);

  const availableOptions = useMemo(() => {
    if (!selectedOptionValues.length) return localOptions;
    return localOptions.filter((option) => !selectedOptionValues.includes(option.value));
  }, [localOptions, selectedOptionValues]);

  const handleChange = useCallback(
    (newSelectedOptions: MultiValue<RSelectOption> | SingleValue<RSelectOption>) => {
      onSelectedOptionChange(newSelectedOptions as RSelectOption[]);
    },
    [onSelectedOptionChange],
  );

  return (
    <div className={classes.poiSearchAutoComplete}>
      <CreatableSelect
        className="trpReactCreatableSelectMulti"
        classNamePrefix="trpReactCreatableSelectMulti"
        onCreateOption={onCreateOption}
        isMulti
        options={availableOptions}
        value={selectedOptions}
        isClearable={false}
        isSearchable
        onChange={handleChange}
        controlShouldRenderValue={false}
        inputValue={inputValue || ''}
        placeholder={placeHolder}
        onInputChange={onInputChange}
        formatCreateLabel={(userInput) => userInput}
        onKeyDown={onKeyDown}
        isDisabled={isDisabled}
        isLoading={isLoading}
        loadingMessage={() => `${t('trips.myTrips.itinerary.offers.qrWriter.loading')}...`}
        noOptionsMessage={() => <div data-no-options-message>{t('trips.myTrips.exploreMore.noResultsFound')}</div>}
        maxMenuHeight={300}
        menuPlacement="bottom"
        menuShouldScrollIntoView
        menuShouldBlockScroll
        components={{ MenuList: VirtualizedMenuList }}
      />
    </div>
  );
};

export default PoiSearchAutoComplete;
